import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Device } from '../../lib/types';
import { useRescueContext } from '../../lib/RescueContext';
import { useToast } from '../../components/Toast/ToastContext';
import {
  RescueDeviceStatsData,
  rescueDeviceStatsQuery,
  rescueCreatePetFosterTransferMutation,
  rescueRecallPetFosterTransferMutation,
  unassignDeviceMutation,
  rescueCreatePetAdoptionTransferMutation,
  rescueCancelPetAdoptionTransferMutation,
  rescueSendSmsMutation,
} from '../../lib/rescueQueries';
import useRescueDevices from '../../hooks/useRescueDevices';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import DevicesTable from './DevicesTable';
import PendingTransfersTable from './PendingTransfersTable';
import CompletedTransfersTable from './CompletedTransfersTable';
import AddPetModal from './AddPetModal';
import TransferToFosterModal from './TransferToFosterModal';
import RecallDeviceModal from './RecallDeviceModal';
import TransferToAdopterModal from './TransferToAdopterModal';
import ResendEmailModal from './ResendEmailModal';
import CancelTransferModal from './CancelTransferModal';
import SendSmsModal from './SendSmsModal';
import {
  trackDeviceTransferToFoster,
  trackDeviceTransferToAdopt,
  trackDeviceRecalled,
  trackDeviceResendEmail,
  trackDeviceCancelTransfer,
  trackDeviceSmsSent,
  trackDevicesOrderClicked,
} from '../../analytics';
import styles from './Devices.module.scss';

const ORDER_DEVICES_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfFmXixSMQDcg9LwQ1_NzkRWsH3uBGWOZg1DdBdfJNL8JsV5Q/viewform';
const PAGE_SIZE = 10;

/** Show completed adoptions from the last 90 days. */
const COMPLETED_SINCE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString();
})();

function LoadingSpinner({ message }: { message?: string }) {
  return (
    <div className={styles.loadingContainer}>
      <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
      </svg>
      {message && <span>{message}</span>}
    </div>
  );
}

export default function Devices() {
  const { orgName } = useRescueContext();
  const { showToast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');

  // ── Org name (from context — resolves chipReseller or RESCUE household) ──

  const resellerName = orgName;

  // ── Stats (unfiltered counts for summary cards) ──

  const { data: statsData } = useQuery<RescueDeviceStatsData>(rescueDeviceStatsQuery, {
    variables: { completedSince: COMPLETED_SINCE },
  });
  const stats = statsData?.rescueDeviceStats;

  // ── Server-side paginated sections ────────

  const rescue = useRescueDevices('IN_RESCUE', searchQuery, PAGE_SIZE, resellerName);
  const foster = useRescueDevices('IN_FOSTER', searchQuery, PAGE_SIZE, resellerName);
  const pending = useRescueDevices('PENDING_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName);
  const completed = useRescueDevices('COMPLETED_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName, COMPLETED_SINCE);

  // ── Loading / Error states ────────────────

  // Only show the full-page loading spinner on initial page load (no search active).
  // During search, Apollo may briefly have empty data for new variables — we don't
  // want to unmount the search bar (which would steal focus from the input).
  const initialLoading =
    !searchQuery &&
    rescue.loading &&
    foster.loading &&
    pending.loading &&
    rescue.devices.length === 0 &&
    foster.devices.length === 0 &&
    pending.devices.length === 0;
  const hasError = rescue.error && foster.error && pending.error;

  // ── T2A Stats ──────────────────────────

  const pendingCount = stats?.pendingAdoptionsCount ?? 0;
  const completedCount = stats?.completedAdoptionsCount ?? 0;
  const totalTransfers = pendingCount + completedCount;
  const showStats = totalTransfers > 0;

  const allTimeRate = totalTransfers > 0 ? Math.round((completedCount / totalTransfers) * 100) : 0;

  function rateColorClass(pct: number): string {
    if (pct >= 80) {
      return styles.summaryValueGreen;
    }
    if (pct >= 60) {
      return styles.summaryValueAmber;
    }
    return styles.summaryValueRed;
  }

  // ── GraphQL mutations ─────────────────────

  const mutationOpts = { refetchQueries: 'active' as const };

  const [createFosterTransferMut] = useMutation(rescueCreatePetFosterTransferMutation, mutationOpts);
  const [recallFosterTransfer] = useMutation(rescueRecallPetFosterTransferMutation, mutationOpts);
  const [unassignDevice] = useMutation(unassignDeviceMutation, mutationOpts);
  const [createAdoptionTransferMut] = useMutation(rescueCreatePetAdoptionTransferMutation, mutationOpts);
  const [cancelAdoptionTransferMut] = useMutation(rescueCancelPetAdoptionTransferMutation, mutationOpts);
  const [sendSmsMut] = useMutation(rescueSendSmsMutation);

  /** Reset all sections to page 1 after a mutation. */
  function resetAllPages() {
    rescue.resetPage();
    foster.resetPage();
    pending.resetPage();
    completed.resetPage();
  }

  // ── Helper: find device across all sections ──

  function findDevice(deviceId: string): Device | undefined {
    return (
      rescue.devices.find((d) => d.id === deviceId) ??
      foster.devices.find((d) => d.id === deviceId) ??
      pending.devices.find((d) => d.id === deviceId) ??
      completed.devices.find((d) => d.id === deviceId)
    );
  }

  // ── Modal state ──────────────────────────

  const [addPetOpen, setAddPetOpen] = useState(false);
  const [addPetDevice, setAddPetDevice] = useState<Device | null>(null);
  const [fosterOpen, setFosterOpen] = useState(false);
  const [fosterDevice, setFosterDevice] = useState<Device | null>(null);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallDevice, setRecallDevice] = useState<Device | null>(null);
  const [adopterOpen, setAdopterOpen] = useState(false);
  const [adopterDevice, setAdopterDevice] = useState<Device | null>(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendDevice, setResendDevice] = useState<Device | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelDevice, setCancelDevice] = useState<Device | null>(null);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsDevice, setSmsDevice] = useState<Device | null>(null);

  // ── Modal handlers ──────────────────────────

  function handleOpenAddPet(device: Device) {
    setAddPetDevice(device);
    setAddPetOpen(true);
  }

  function handleOpenFoster(device: Device) {
    setFosterDevice(device);
    setFosterOpen(true);
  }

  function handleOpenRecall(device: Device) {
    setRecallDevice(device);
    setRecallOpen(true);
  }

  function handleOpenAdopter(device: Device) {
    setAdopterDevice(device);
    setAdopterOpen(true);
  }

  function handleOpenResend(device: Device) {
    setResendDevice(device);
    setResendOpen(true);
  }

  function handleOpenCancel(device: Device) {
    setCancelDevice(device);
    setCancelOpen(true);
  }

  function handleOpenSms(device: Device) {
    setSmsDevice(device);
    setSmsOpen(true);
  }

  // ── Mutation handlers ──────────────────────────

  function handleAddPet(_deviceId: string, petName: string) {
    void petName;
    showToast(`Pet name update is not yet supported via the API`);
  }

  async function handleTransferToFoster(
    deviceId: string,
    firstName: string,
    lastName: string,
    fosterEmail: string,
    fosterPhone: string,
  ) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    try {
      await createFosterTransferMut({
        variables: {
          input: {
            petId: device._petGraphqlId,
            email: fosterEmail,
            firstName,
            lastName: lastName || undefined,
            phoneNumber: fosterPhone,
          },
        },
      });
      trackDeviceTransferToFoster();
      resetAllPages();
      showToast(`${device.id} assigned to foster`);
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleRecall(deviceId: string) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    if (device.holderType === 'foster') {
      // Foster recall — revoke the foster transfer
      try {
        await recallFosterTransfer({
          variables: {
            input: { petId: device._petGraphqlId },
          },
        });
        trackDeviceRecalled();
        resetAllPages();
        showToast(`${device.id} recalled to rescue`);
      } catch (err) {
        showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    } else {
      // Non-foster — just unassign the device from the pet
      try {
        await unassignDevice({
          variables: {
            moduleId: device.id,
            petId: device._petGraphqlId,
          },
        });
        resetAllPages();
        showToast(`Pet removed from ${device.id}`);
      } catch (err) {
        showToast(`Failed to remove pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  }

  async function handleTransferToAdopter(
    deviceId: string,
    firstName: string,
    lastName: string,
    adopterEmail: string,
    adopterPhone: string,
  ) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    try {
      await createAdoptionTransferMut({
        variables: {
          input: {
            petId: device._petGraphqlId,
            firstName,
            lastName: lastName || undefined,
            email: adopterEmail || undefined,
            phoneNumber: adopterPhone,
          },
        },
      });
      trackDeviceTransferToAdopt();
      resetAllPages();
      showToast(`Transfer initiated for ${device.id}`);
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleCancelAdoptionTransfer(device: Device) {
    try {
      await cancelAdoptionTransferMut({
        variables: {
          input: { petId: device._petGraphqlId },
        },
      });
      trackDeviceCancelTransfer();
      resetAllPages();
      showToast(`Adoption transfer cancelled for ${device.id}`);
    } catch (err) {
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleSendSms(phoneNumber: string, message: string) {
    try {
      await sendSmsMut({ variables: { input: { phoneNumber, message } } });
      trackDeviceSmsSent();
      showToast('SMS sent successfully');
    } catch (err) {
      showToast(`Failed to send SMS: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleResendEmail(deviceId: string) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    const to = device.adopterEmail ?? '';
    const subject = encodeURIComponent(`Set up your Fi collar${device.petName ? ` for ${device.petName}` : ''}`);
    const body = encodeURIComponent(
      `Hi${device.adopterName ? ` ${device.adopterName}` : ''},\n\nCongratulations on your adoption${
        device.petName ? ` of ${device.petName}` : ''
      }! Your new companion comes with a Fi GPS collar (${
        device.id
      }).\n\nTo activate the collar and start tracking, download the Fi app and follow the setup instructions:\nhttps://tryfi.com/app\n\nIf you have any questions, feel free to reach out to us or the Fi team at support@tryfi.com.\n\nBest,\n${orgName}`,
    );
    trackDeviceResendEmail();
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }

  // ── Order Devices ──────────────────────────

  function handleOrderDevices() {
    trackDevicesOrderClicked();
    window.open(ORDER_DEVICES_FORM_URL, '_blank', 'noopener,noreferrer');
  }

  // ── Render ──────────────────────────

  if (initialLoading) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner message="Loading devices..." />
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <div className={styles.errorContainer}>
          <p>Failed to load devices. Please try again.</p>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1>Fi Devices</h1>
        <div className={styles.headerActions}>
          <Button variant="green" onClick={handleOrderDevices}>
            <svg
              className={styles.headerBtnIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Order Devices
          </Button>
        </div>
      </div>

      {/* T2A Summary Stats */}
      {showStats && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Pending Transfers</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueAmber}`}>{pendingCount}</div>
            <div className={styles.summaryCardSubtext}>awaiting adopter activation</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Transfer to Adoption Rate</div>
            <div className={`${styles.summaryCardValue} ${rateColorClass(allTimeRate)}`}>{allTimeRate}%</div>
            <div className={styles.summaryCardSubtext}>
              {completedCount} completed, {pendingCount} pending
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Total Fi Pets</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueGreen}`}>{completedCount}</div>
            <div className={styles.summaryCardSubtext}>adopted through your rescue</div>
          </div>
        </div>
      )}

      {/* Search */}
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by device ID, pet name, or holder..."
      />

      {/* Pending Transfers */}
      <PendingTransfersTable
        devices={pending.devices}
        totalCount={pending.totalCount}
        currentPage={pending.currentPage}
        totalPages={pending.totalPages}
        pageSize={PAGE_SIZE}
        onPageChange={pending.goToPage}
        loading={pending.loading}
        skeletonCount={pending.skeletonCount}
        onResendEmail={handleOpenResend}
        onCancelTransfer={handleOpenCancel}
        onSendSms={handleOpenSms}
      />

      {/* Active Devices (Rescue + Foster) */}
      <DevicesTable
        rescueDevices={rescue.devices}
        fosterDevices={foster.devices}
        rescueTotalCount={rescue.totalCount}
        fosterTotalCount={foster.totalCount}
        rescueCurrentPage={rescue.currentPage}
        rescueTotalPages={rescue.totalPages}
        fosterCurrentPage={foster.currentPage}
        fosterTotalPages={foster.totalPages}
        pageSize={PAGE_SIZE}
        onRescuePageChange={rescue.goToPage}
        onFosterPageChange={foster.goToPage}
        rescueLoading={rescue.loading}
        fosterLoading={foster.loading}
        rescueSkeletonCount={rescue.skeletonCount}
        fosterSkeletonCount={foster.skeletonCount}
        searchQuery={searchQuery}
        onAddPet={handleOpenAddPet}
        onTransferFoster={handleOpenFoster}
        onRecall={handleOpenRecall}
        onTransferAdopter={handleOpenAdopter}
        onSendSms={handleOpenSms}
      />

      {/* Completed Transfers */}
      <CompletedTransfersTable
        devices={completed.devices}
        totalCount={completed.totalCount}
        currentPage={completed.currentPage}
        totalPages={completed.totalPages}
        pageSize={PAGE_SIZE}
        onPageChange={completed.goToPage}
        loading={completed.loading}
        skeletonCount={completed.skeletonCount}
      />

      {/* Modals */}
      <AddPetModal
        open={addPetOpen}
        onClose={() => setAddPetOpen(false)}
        device={addPetDevice}
        onAddPet={handleAddPet}
      />
      <TransferToFosterModal
        open={fosterOpen}
        onClose={() => setFosterOpen(false)}
        device={fosterDevice}
        onTransfer={handleTransferToFoster}
      />
      <RecallDeviceModal
        open={recallOpen}
        onClose={() => setRecallOpen(false)}
        device={recallDevice}
        onRecall={handleRecall}
      />
      <TransferToAdopterModal
        open={adopterOpen}
        onClose={() => setAdopterOpen(false)}
        device={adopterDevice}
        onTransfer={handleTransferToAdopter}
      />
      <ResendEmailModal
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        device={resendDevice}
        onResend={handleResendEmail}
      />
      <CancelTransferModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        device={cancelDevice}
        onConfirm={handleCancelAdoptionTransfer}
      />
      <SendSmsModal
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        device={smsDevice}
        orgName={orgName}
        onSendSms={handleSendSms}
      />
    </>
  );
}
